import { MDSLNBItem } from '@marqvision/mds-v2';
import { useUserInfoQuery } from '~/apis/auth';
import { AnalyticsRouteEnum, PortfolioRouteEnum, RootRouteEnum } from '~/router/@types';
import { PermissionMap } from '~/router/permissionMap';
import { isAllowedPiracyAssetsAsSecondary, RevolutClientId } from '~/utils';

export const getAllowedRoutes = async <Route extends Partial<MDSLNBItem>>(
  routes: Route[],
  option?: { brandId?: number }
): Promise<Route[]> => {
  return await routes.reduce<Promise<Route[]>>(async (prev, route) => {
    const filteredRoute = await getAllowedRoute(route, option);
    const prevRoutes = await prev;
    if (filteredRoute) return Promise.resolve([...prevRoutes, filteredRoute]);
    return prev;
  }, Promise.resolve([]));
};

const getAllowedRoute = async <Route extends Partial<MDSLNBItem>>(
  route: Route,
  option?: { brandId?: number }
): Promise<Route | undefined> => {
  if (route.items) {
    const filteredItems = await getAllowedRoutes(route.items, option);
    const newRoute: Route | undefined = filteredItems.length > 0 ? { ...route, items: filteredItems } : undefined;
    switch (route.key) {
      case RootRouteEnum.Analytics:
        return (await PermissionMap.Analytics()) ? newRoute : undefined;
      case RootRouteEnum.Portfolio:
        return (await PermissionMap.Portfolio()) ? newRoute : undefined;
      case RootRouteEnum.Marketplace:
        return (await PermissionMap.Marketplace()) ? newRoute : undefined;
      case 'all-social-media':
        return (await PermissionMap.AllSocialMedia()) ? newRoute : undefined;
      case RootRouteEnum.SocialMedia:
        return (await PermissionMap.SocialMedia()) ? newRoute : undefined;
      case 'piracy-social-media':
        return (await PermissionMap.PiracySocialMedia()) ? newRoute : undefined;
      case RootRouteEnum.PriceMonitoring:
        return (await PermissionMap.PriceMonitoring()) ? newRoute : undefined;
      default:
        return newRoute;
    }
  } else {
    switch (route.key) {
      case RootRouteEnum.Home:
        return (await PermissionMap.Home()) ? route : undefined;
      case AnalyticsRouteEnum.CustomerReports:
        return (await PermissionMap.PerformanceReport()) ? route : undefined;
      case AnalyticsRouteEnum.BrandExposure:
        return (await PermissionMap.PerformanceBrandExposure()) ? route : undefined;
      case PortfolioRouteEnum.Assets:
        return (await PermissionMap.PortfolioAssets()) ? route : undefined;
      case `${PortfolioRouteEnum.Assets}/counterfeit`:
        return (await PermissionMap.PortfolioCounterfeitAssets()) ? route : undefined;
      case `${PortfolioRouteEnum.Assets}/piracy`:
        return (await PermissionMap.PortfolioPiracyAssets()) ? route : undefined;
      case PortfolioRouteEnum.CopyrightedImages:
        return option?.brandId ? { ...route, path: `${route.path}/${option.brandId}` } : route;
      case PortfolioRouteEnum.InfringingImages:
        return (await PermissionMap.PortfolioInfringingImages()) ? route : undefined;
      case PortfolioRouteEnum.AuthorizedList:
        return (await PermissionMap.PortfolioAuthorizedSellers()) ? route : undefined;
      case PortfolioRouteEnum.Models:
        return (await PermissionMap.PortfolioModels()) ? route : undefined;
      case RootRouteEnum.Marketplace:
        return (await PermissionMap.Marketplace()) ? route : undefined;
      case 'all-social-media':
        return (await PermissionMap.AllSocialMedia()) ? route : undefined;
      case RootRouteEnum.SocialMedia:
        return (await PermissionMap.SocialMedia()) ? route : undefined;
      case 'piracy-social-media':
        return (await PermissionMap.PiracySocialMedia()) ? route : undefined;
      case RootRouteEnum.WebsiteContent:
        return (await PermissionMap.WebsiteContent()) ? route : undefined;
      case RootRouteEnum.DomainNames:
        return (await PermissionMap.DomainNames()) ? route : undefined;
      case RootRouteEnum.SellerAnalytics:
      case RootRouteEnum.SellerLink:
      case RootRouteEnum.SellerIntel:
        return (await PermissionMap.Seller()) ? route : undefined;
      case RootRouteEnum.ConversationalAssistant:
        return (await PermissionMap.ConversationalAssistant()) ? route : undefined;
      case RootRouteEnum.GenerativeAI:
        return (await PermissionMap.GenerativeAI()) ? route : undefined;
      default:
        return route;
    }
  }
};

export const changeRouteName = (routes: MDSLNBItem[][], userData: ReturnType<typeof useUserInfoQuery>['data']) => {
  const newRoutes = [...routes];

  newRoutes.forEach((routeGroup) => {
    routeGroup.forEach((route) => {
      // note-@blue-poolyum: Revolut 고객사에 한해 Social media의 Counterfeit Listings를 Impersonation Listings로 변경 (추후 이런 하드코딩 정리 예정 ㅠㅠㅠㅠ)
      if (userData?.client_id === RevolutClientId && route.key === 'all-social-media') {
        route.items?.forEach((item) => {
          if (item.label === 'Counterfeit Listings') {
            item.label = 'Impersonation Listings';
          }
        });
      }
      // note-@blue-poolyum: Asset 둘다 보여주는 고객 순서와 이름 정리 (기존 로직은 라이엇에 맞춰져 있어서 이런 로직이.......) (추후 이런 하드코딩 정리 예정 ㅠㅠㅠㅠ)
      else if (isAllowedPiracyAssetsAsSecondary(userData) && route.key === 'portfolio') {
        route.items?.forEach((item) => {
          if (item.key === 'assets/piracy') {
            item.label = 'Content assets';
          }
          if (item.key === 'assets/counterfeit') {
            item.label = 'Assets';
          }
        });

        // swap 'assets/counterfeit' and 'assets/piracy' order
        if (route.items) {
          const piracyIndex = route.items.findIndex((item) => item.key === 'assets/piracy');
          const counterfeitIndex = route.items.findIndex((item) => item.key === 'assets/counterfeit');

          if (piracyIndex < counterfeitIndex && piracyIndex !== -1 && counterfeitIndex !== -1) {
            const temp = route.items[piracyIndex];
            route.items[piracyIndex] = route.items[counterfeitIndex];
            route.items[counterfeitIndex] = temp;
          }
        }
      }
    });
  });

  return newRoutes;
};
